/**
 * Copyright (c) 2025-2026 eBUCIS, LLC. All rights reserved.
 *
 * usePerSubjectScores - React hook for per-subject SAT score predictions
 *
 * Provides separate current and predicted scores for Math vs Reading & Writing,
 * supporting the reflective dashboard design with trend analysis and sharing.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import predictionService from '../services/predictionService'

import { createLogger } from '../utils/logger'

const logger = createLogger('usePerSubjectScores')

/**
 * Maps API errors to user-friendly messages
 */
const getErrorMessage = (err) => {
  if (err.response?.status === 401) return 'Please sign in to view scores'
  if (err.response?.status === 403) return 'Permission denied'
  if (err.response?.status >= 500) return 'Server error. Please try again later'
  if (err.code === 'ERR_NETWORK') return 'Network error. Please check your connection'
  return err.response?.data?.message || 'Unable to load scores'
}

/**
 * Hook for fetching per-subject (Math vs R&W) scores with current and predicted values
 *
 * @param {Object} options - Hook configuration
 * @param {boolean} options.autoFetch - Whether to fetch on mount (default: true)
 * @param {number} options.refreshInterval - Auto-refresh interval in ms (0 = disabled)
 * @returns {Object} Per-subject scores state and methods
 */
export function usePerSubjectScores({ autoFetch = true, refreshInterval = 0 } = {}) {
  // State
  const [data, setData] = useState({
    hasEnoughData: false,
    math: {
      hasData: false,
      currentScore: null,
      predictedScore: null,
      confidence: null,
      confidenceLevel: null,
      trend: null
    },
    readingWriting: {
      hasData: false,
      currentScore: null,
      predictedScore: null,
      confidence: null,
      confidenceLevel: null,
      trend: null
    },
    combined: {
      currentScore: null,
      predictedScore: null,
      trend: null
    }
  })
  const [loading, setLoading] = useState(autoFetch)
  const [error, setError] = useState(null)
  const [lastFetched, setLastFetched] = useState(null)

  // Refs
  const refreshIntervalRef = useRef(null)
  const isMountedRef = useRef(true)
  const requestIdRef = useRef(0)

  /**
   * Fetch per-subject scores
   */
  const fetchScores = useCallback(async () => {
    if (!isMountedRef.current) return

    const currentRequestId = ++requestIdRef.current

    setLoading(true)
    setError(null)

    try {
      const response = await predictionService.getPerSubjectScores()

      if (isMountedRef.current && currentRequestId === requestIdRef.current) {
        setData(
          response?.data ||
            response || {
              hasEnoughData: false,
              math: { hasData: false },
              readingWriting: { hasData: false },
              combined: {}
            }
        )
        setLastFetched(new Date())
      }
    } catch (err) {
      logger.error('Error fetching per-subject scores:', err)
      if (isMountedRef.current && currentRequestId === requestIdRef.current) {
        setError(getErrorMessage(err))
      }
    } finally {
      if (isMountedRef.current && currentRequestId === requestIdRef.current) {
        setLoading(false)
      }
    }
  }, [])

  /**
   * Refresh scores
   */
  const refresh = useCallback(() => {
    return fetchScores()
  }, [fetchScores])

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch) {
      fetchScores()
    }
  }, [autoFetch, fetchScores])

  // Set up refresh interval
  useEffect(() => {
    if (refreshInterval > 0) {
      refreshIntervalRef.current = setInterval(fetchScores, refreshInterval)
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current)
        refreshIntervalRef.current = null
      }
    }
  }, [refreshInterval, fetchScores])

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  return {
    // Full data object
    data,
    loading,
    error,
    lastFetched,
    refresh,

    // Convenience accessors
    hasEnoughData: data?.hasEnoughData ?? false,

    // Math section
    math: data?.math ?? { hasData: false },
    mathHasData: data?.math?.hasData ?? false,
    mathCurrentScore: data?.math?.currentScore ?? null,
    mathPredictedScore: data?.math?.predictedScore ?? null,
    mathTrend: data?.math?.trend ?? null,

    // R&W section
    readingWriting: data?.readingWriting ?? { hasData: false },
    rwHasData: data?.readingWriting?.hasData ?? false,
    rwCurrentScore: data?.readingWriting?.currentScore ?? null,
    rwPredictedScore: data?.readingWriting?.predictedScore ?? null,
    rwTrend: data?.readingWriting?.trend ?? null,

    // Combined
    combined: data?.combined ?? {},
    combinedCurrentScore: data?.combined?.currentScore ?? null,
    combinedPredictedScore: data?.combined?.predictedScore ?? null,
    combinedTrend: data?.combined?.trend ?? null
  }
}

/**
 * Hook for fetching share card data
 *
 * @param {Object} options - Hook configuration
 * @param {boolean} options.autoFetch - Whether to fetch on mount (default: false)
 * @returns {Object} Share card data and methods
 */
export function useShareCardData({ autoFetch = false } = {}) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(autoFetch)
  const [error, setError] = useState(null)

  const isMountedRef = useRef(true)
  const requestIdRef = useRef(0)

  const fetchShareData = useCallback(async () => {
    if (!isMountedRef.current) return

    const currentRequestId = ++requestIdRef.current

    setLoading(true)
    setError(null)

    try {
      const response = await predictionService.getShareCardData()

      if (isMountedRef.current && currentRequestId === requestIdRef.current) {
        setData(response?.data || response)
      }
    } catch (err) {
      logger.error('Error fetching share card data:', err)
      if (isMountedRef.current && currentRequestId === requestIdRef.current) {
        setError(getErrorMessage(err))
      }
    } finally {
      if (isMountedRef.current && currentRequestId === requestIdRef.current) {
        setLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    if (autoFetch) {
      fetchShareData()
    }
  }, [autoFetch, fetchShareData])

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  return {
    data,
    loading,
    error,
    fetch: fetchShareData,
    refresh: fetchShareData,

    // Convenience accessors
    scores: data?.scores ?? null,
    badgeCount: data?.badgeCount ?? 0,
    highestLevel: data?.highestLevel ?? 1,
    generatedAt: data?.generatedAt ?? null,

    // Enhanced share data (new fields)
    topBadges: data?.topBadges ?? [],
    streak: data?.streak ?? { currentStreak: 0, longestStreak: 0, isActiveToday: false },
    goal: data?.goal ?? null,
    totalChallenges: data?.totalChallenges ?? 0,
    confidenceLevels: data?.confidenceLevels ?? { math: null, rw: null },
    publicName: data?.publicName ?? null
  }
}

export default usePerSubjectScores
